import { useEffect, useRef, useState } from 'react'
import type { ColumnDef, SortKey } from './matrixColumns'

interface Props {
  columns: ColumnDef[]
  hidden: Set<SortKey>
  onChange: (next: Set<SortKey>) => void
}

export default function ColumnVisibilityMenu({ columns, hidden, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  function toggle(key: SortKey) {
    const next = new Set(hidden)
    if (next.has(key)) next.delete(key); else next.add(key)
    onChange(next)
  }

  function showAll() {
    onChange(new Set())
  }

  const toggleable = columns.filter(c => !c.alwaysVisible)
  const hiddenCount = toggleable.filter(c => hidden.has(c.key)).length

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors flex items-center gap-1.5 ${
          hiddenCount > 0
            ? 'bg-brand-600/15 text-brand-700 dark:text-brand-300 border-brand-600/40'
            : 'bg-sunken hover:bg-line text-ink-muted border-line'
        }`}
        title="Mostrar / ocultar colunas da matriz"
      >
        <span>⚙ Colunas</span>
        {hiddenCount > 0 && (
          <span className="bg-black/30 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums">
            {hiddenCount} oculta{hiddenCount === 1 ? '' : 's'}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-64 max-h-[60vh] overflow-auto bg-surface border border-line rounded-lg shadow-xl z-30 p-2">
          <div className="flex items-center justify-between px-2 py-1.5 mb-1 border-b border-line">
            <span className="text-xs text-ink-muted font-semibold uppercase">Colunas visíveis</span>
            <button
              onClick={showAll}
              disabled={hiddenCount === 0}
              className="text-[10px] px-2 py-0.5 rounded bg-sunken hover:bg-line disabled:opacity-40 text-ink-muted"
            >
              Mostrar todas
            </button>
          </div>
          {toggleable.map(c => {
            const isHidden = hidden.has(c.key)
            return (
              <label
                key={c.key}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-sunken rounded cursor-pointer text-sm text-ink"
              >
                <input
                  type="checkbox"
                  checked={!isHidden}
                  onChange={() => toggle(c.key)}
                  className="accent-brand-500"
                />
                <span className={isHidden ? 'opacity-60' : ''}>{c.label}</span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}
